import { API_PREFIX_BLOCK_CONTENT_PATH, API_PREFIX_PAGE_PATH } from "@/constants/prefix-path-api"
import { message } from "antd"
import { AxiosRequestConfig } from "axios"
import { t } from "i18next"
import { useState } from "react"
import { HttpClient } from "../../../../app/api/HttpClient"
import { dataPayloadBlockContent, dataPayloadPage, FilterSearchParams } from "../../ui-builder/config/types"

const pageService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const addPage = async (data: { data: dataPayloadPage }) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(`${API_PREFIX_PAGE_PATH}`, data)
      return response
    } catch (error) {
      message.error(t("Unable to add page. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPageById = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}/${id}?populate=*`)
      return response?.data || null
    } catch (error) {
      message.error(t("Failed to fetch page data. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updatePage = async (id: number, data: { data: dataPayloadPage }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.put(`${API_PREFIX_PAGE_PATH}/${id}`, data)
      message.success("Trang được cập nhật thành công!")
      return response
    } catch (error) {
      message.error(t("Unable to update page. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deletePage = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.delete(`${API_PREFIX_PAGE_PATH}/${id}`)
      return response
    } catch (error) {
      message.error(t(t("Unable to update page. Please try again")))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllPages = async (searchTerms: { [key: string]: string } = {}) => {
    const params: FilterSearchParams = {
      populate: "*",
      sort: "createdAt:desc",
      publicationState: "preview",
      filters: {},
      pagination: {
        pageSize: "100",
      },
      locale: "all",
    }

    const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

    Object.keys(searchTerms).forEach(key => {
      if (!excludedKeys.includes(key)) {
        const value = searchTerms[key]

        if (key === "publicationState") {
          const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
          params.publicationState = convertParamStatusPost

          if (value === "draft") {
            params.filters.publishedAt = { $null: true }
          } else {
            delete params.filters.publishedAt
          }
        } else if (key === "locale") {
          params.locale = value
        } else if (!excludedKeys.includes(key)) {
          if (value) {
            params.filters[key] = { $containsi: value }
          }
        } else if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      } else {
        const value = searchTerms[key]
        if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      }
    })

    try {
      const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, { params })
      const modifiedResponse = response.data.map((page: any) => ({
        ...page,
        status: page.attributes.publishedAt ? "published" : "draft",
        publishedAt: page.attributes.publishedAt,
      }))

      return {
        data: modifiedResponse,
        meta: response.meta,
      }
    } catch (error) {
      message.error(t("Failed to fetch page. Please try again"))
    }
  }

  const checkSlug = async (slug: string) => {
    const params: AxiosRequestConfig["params"] = {
      filters: {
        slug: { $eq: slug },
      },
      publicationState: "preview",
    }

    return await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, { params })
  }

  const createLocalization = async (id: number, data: { data: dataPayloadPage & { locale?: string } }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Invalid ID"))
      setIsLoading(false)
      return null
    }

    try {
      const payload = data.data
      const response = await HttpClient.post(`${API_PREFIX_PAGE_PATH}/${id}/localizations`, payload)
      message.success(t("Localization created successfully!"))
      return response
    } catch (error) {
      console.error("Localization error:", error)
      message.error(t("Unable to create localization. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const createLocalizationBlockContent = async (id: number, data: { data: dataPayloadBlockContent & { locale?: string } }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Invalid ID"))
      setIsLoading(false)
      return null
    }

    try {
      const payloadBlockContent = {
        ...data.data,
      }

      const response = await HttpClient.post(`${API_PREFIX_BLOCK_CONTENT_PATH}/${id}/localizations`, payloadBlockContent)
      message.success(t("Localization created successfully!"))
      return response
    } catch (error) {
      console.error("Localization error:", error)
      message.error(t("Unable to create localization. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    addPage,
    fetchPageById,
    updatePage,
    deletePage,
    fetchAllPages,
    checkSlug,
    createLocalization,
    createLocalizationBlockContent,
  }
}

export default pageService
